<script>
import { mapGetters, mapMutations, mapActions } from "vuex";
import moment from "moment";
import ModalError from "@/components/modals/ModalError";
import VueAnnouncer from "@vue-a11y/announcer";
import App from "@/apps/midttrafik/App.vue";

import {
    FLOW_ERROR_NOT_STARTED,
    FLOW_ERROR_ZONE_DISTANCE_NO_TRIP,
    FLOW_ERROR_STOP_IDENTITY_CONFLICT,
    FLOW_ERROR_TICKET_NOT_SELLABLE,
    FLOW_ERROR_TOO_LATE_TO_ORDER_TICKET,
    FLOW_ERROR_PRODUCT_NOT_AVAILABLE,
    FLOW_ERROR_SERVICE_UNAVAILABLE,
    FLOW_ERROR_DEPARTURE_DATE_INVALID,
    FLOW_ERROR_CONNECTION_FAILED,
} from "@/store/modules/flow";

export default {
    name: "Bootstrap",
    components: {
        VueAnnouncer: VueAnnouncer.default,
        App,
        ModalError,
    },
    data() {
        return {
            bootstrapped: false,
            configLoading: true,
            pageLoading: false,
            pageLoadingMessage: null,
        };
    },
    computed: {
        loading() {
            return Boolean(
                this.configLoading || this.flowLoading || this.pageLoading
            );
        },
        loadingMessage() {
            if (typeof this.flowLoading === "string") {
                return this.$t(
                    `loading_message.${this.flowLoading.toLowerCase()}`
                );
            }

            if (this.pageLoadingMessage) {
                return this.pageLoadingMessage;
            }

            return "";
        },
        ...mapGetters({
            errors: "error/getMustShowErrors",
            passengerTypes: "passenger/getPassengerTypes",
            serverDate: "date/getServerDateAsMoment",
            flowStep: "flow/getFullStep",
            flowLoading: "flow/isLoading",
        }),
    },
    watch: {
        flowStep: function ([type, step]) {
            if (!type) return;

            const match = this.$router.flowToRoute(type, step);
            if (match && match.name !== this.$route.name) {
                this.$router.push({ path: match.path });
            }
        },
    },
    mounted() {
        this.systemLoad()
            .then(() => {
                // bootstrap from rejseplanen app links specification 1.2
                const {
                    path,
                    query: {
                        startStopRejseplanenId,
                        endStopRejseplanenId,
                        departureTime,
                        rejseplanenPassengerType,
                    },
                } = this.$route;
                if (
                    path === "/" &&
                    startStopRejseplanenId &&
                    endStopRejseplanenId &&
                    departureTime
                ) {
                    const validatedStopTime = moment(departureTime).isBefore(
                        this.serverDate
                    )
                        ? this.serverDate
                        : moment(departureTime);
                    this.setDepartureDate({ departureDate: validatedStopTime });
                    const passengerType = this.passengerTypes.filter(
                        (t) =>
                            t.rejseplanenIdentifier ===
                            (rejseplanenPassengerType || "A")
                    );
                    if (passengerType.length) {
                        this.updatePassengers({
                            passengerTypeIds: this.passengerTypes.map(
                                (p) => p.id
                            ),
                            primaryId: passengerType[0].id,
                        });
                    }
                    return this.setStopsByRejseplanenIds({
                        stopFromId: startStopRejseplanenId,
                        stopToId: endStopRejseplanenId,
                    });
                }
                return null;
            })
            .then(() => {
                this.bootstrapped = true;
                this.configLoading = false;
            })
            .catch((error) => {
                setTimeout(() => {
                    throw error;
                });
            });
    },
    errorCaptured(error) {
        switch (error) {
            case FLOW_ERROR_NOT_STARTED:
                this.$router.replace("/");
                return false;
            case FLOW_ERROR_STOP_IDENTITY_CONFLICT:
                this.setError({ type: "no_trip" });
                return false;
            case FLOW_ERROR_ZONE_DISTANCE_NO_TRIP:
                this.setError({ type: "no_trip" });
                return false;
            case FLOW_ERROR_TICKET_NOT_SELLABLE:
            case FLOW_ERROR_DEPARTURE_DATE_INVALID:
            case FLOW_ERROR_PRODUCT_NOT_AVAILABLE:
                this.setError({ type: "ticket_not_sellable" });
                return false;
            case FLOW_ERROR_TOO_LATE_TO_ORDER_TICKET:
                this.setError({ type: "ticket_not_sellable", hard: true });
                return false;
            case FLOW_ERROR_SERVICE_UNAVAILABLE:
                this.setError({ type: "service_unavailable" });
                return false;
            case FLOW_ERROR_CONNECTION_FAILED:
                this.setError({ type: "connection_failed", hard: true });
                return false;
            default:
                // let the error handler set the error
                return true;
        }
    },
    methods: {
        handlePageLoading(isLoading, message) {
            this.pageLoading = isLoading;
            this.pageLoadingMessage = isLoading ? message : null;
        },
        ...mapMutations({
            updatePassengers: "passenger/updatePassengers",
            setError: "error/setError",
        }),
        ...mapActions({
            systemLoad: "system/loadConfig",
            setDepartureDate: "date/setDepartureDate",
            setStopsByRejseplanenIds: "location/setStopsByRejseplanenIds",
        }),
    },
};
</script>

<template>
    <app
        :bootstrapped="bootstrapped"
        :loading="loading"
        :loading-message="loadingMessage"
        :on-loading="handlePageLoading"
    >
        <VueAnnouncer />
        <modal-error v-if="errors" />
    </app>
</template>

<style scoped></style>
